import { useEffect, useState } from "react";
import { Star, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import { useSite } from "@/context/SiteContext";
import { api, errorText } from "@/lib/api";
import { IMG } from "@/lib/catalog";
import { ReviewCard, GoogleReviews, useGoogleReviews } from "@/components/HomeExtras";
import { GoogleReviewQR } from "@/components/GoogleReviewQR";

const field = "w-full rounded-sm border border-line bg-surface px-3 py-2.5 text-sm outline-none focus:border-gold";

const Stars = ({ value, onChange }) => (
  <div className="flex gap-1" role={onChange ? "radiogroup" : undefined} aria-label={`${value} de 5 estrellas`}>
    {[1, 2, 3, 4, 5].map((n) => onChange ? (
      <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} estrellas`} onClick={() => onChange(n)} data-testid={`review-star-${n}`}>
        <Star size={26} className="text-gold" fill={n <= value ? "currentColor" : "none"} />
      </button>
    ) : <Star key={n} size={16} className="text-gold" fill={n <= value ? "currentColor" : "none"} />)}
  </div>
);

const ReviewForm = () => {
  const [f, setF] = useState({ name: "", vehicle: "", rating: 5, comment: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", f);
      toast.success("¡Gracias! Tu opinión se publicará una vez revisada.");
      setF({ name: "", vehicle: "", rating: 5, comment: "" });
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="card-dark space-y-4 p-6" data-testid="review-form">
      <h2 className="text-2xl font-bold uppercase">Deja tu comentario aquí</h2>
      <Stars value={f.rating} onChange={(rating) => setF({ ...f, rating })} />
      <label className="block text-xs uppercase tracking-wider text-mute">Nombre<input className={`${field} mt-1`} required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name-input" /></label>
      <label className="block text-xs uppercase tracking-wider text-mute">Vehículo (opcional)<input className={`${field} mt-1`} value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })} data-testid="review-vehicle-input" /></label>
      <label className="block text-xs uppercase tracking-wider text-mute">Comentario<textarea className={`${field} mt-1`} rows={4} required minLength={5} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="review-comment-input" /></label>
      <button className="btn-gold w-full" disabled={busy} data-testid="review-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />} Enviar opinión</button>
    </form>
  );
};

export default function Reviews() {
  useSeo("Opiniones", "Lee y deja opiniones sobre el Taller de Suspensiones La Gringa en Mérida.");
  const { site } = useSite() || {};
  const gr = useGoogleReviews();
  const [reviews, setReviews] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([])); }, []);
  const b = site?.business;
  return (
    <>
      <PageHero eyebrow="Opiniones" title="Lo que dicen nuestros clientes" image={IMG.assembly} subtitle="Las opiniones de Google se leen directamente en Google Maps. También puedes dejarnos un comentario aquí.">
        <a href={b?.google_review_url} target="_blank" rel="noopener noreferrer" className="btn-gold !px-8 !py-4 !text-base" data-testid="reviews-google-review-btn"><Star size={18} /> Dejar opinión en Google</a>
        <a href={b?.google_maps_url} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="reviews-read-google-btn"><ExternalLink size={16} /> Leer opiniones en Google</a>
      </PageHero>
      <GoogleReviewQR id="reviews-qr" />
      {gr?.configured && gr.reviews?.length > 0 && <section className="container-x pt-16"><h2 className="eyebrow mb-6">Reseñas de Google</h2><GoogleReviews g={gr} limit={5} /></section>}
      <section className="container-x grid gap-10 py-16 lg:grid-cols-[1fr,380px]">
        <div>
          <h2 className="eyebrow mb-6">Comentarios en este sitio</h2>
          {reviews === null && <Loader2 className="animate-spin text-gold" />}
          {reviews?.length === 0 && <p className="text-mute" data-testid="reviews-empty">Aún no hay comentarios publicados. ¡Sé el primero!</p>}
          <div className="grid gap-5 sm:grid-cols-2" data-testid="reviews-list">
            {reviews?.map((r) => <ReviewCard key={r.id} r={r} />)}
          </div>
        </div>
        <ReviewForm />
      </section>
    </>
  );
}
